/**
 * Scroll-linked solid header underlay — opacity driven by `scrollY` on the UI thread.
 */
import React from 'react';
import { StyleSheet } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  type SharedValue,
} from 'react-native-reanimated';
import {
  CHROME_REVEAL_OPACITY_MAX,
  CHROME_REVEAL_SCROLL_THRESHOLD,
  resolveChromeRevealInputRange,
} from '../../shared/theme/styles/headerTokens';

export type ScrollChromeMode = 'off' | 'reveal' | 'always';

type ScrollChromeUnderlayProps = {
  scrollY: SharedValue<number>;
  backgroundColor: string;
  borderColor?: string;
  threshold?: number;
  maxOpacity?: number;
  enabled?: boolean;
};

export function useScrollChromeOpacityStyle(
  scroll_y: SharedValue<number>,
  threshold = CHROME_REVEAL_SCROLL_THRESHOLD,
  max_opacity = CHROME_REVEAL_OPACITY_MAX,
  enabled = true,
) {
  const input_range = resolveChromeRevealInputRange(threshold);

  return useAnimatedStyle(() => {
    'worklet';
    if (!enabled) {
      return { opacity: 0 };
    }
    return {
      opacity: interpolate(
        scroll_y.value,
        input_range,
        [0, 0, max_opacity],
        Extrapolation.CLAMP,
      ),
    };
  }, [enabled, threshold, max_opacity]);
}

export function ScrollChromeUnderlay({
  scrollY,
  backgroundColor,
  borderColor,
  threshold = CHROME_REVEAL_SCROLL_THRESHOLD,
  maxOpacity = CHROME_REVEAL_OPACITY_MAX,
  enabled = true,
}: ScrollChromeUnderlayProps) {
  const opacity_style = useScrollChromeOpacityStyle(scrollY, threshold, maxOpacity, enabled);

  return (
    <>
      <Animated.View
        pointerEvents="none"
        collapsable={false}
        style={[StyleSheet.absoluteFillObject, { backgroundColor }, opacity_style]}
      />
      {borderColor ? (
        <Animated.View
          pointerEvents="none"
          collapsable={false}
          style={[styles.border, { backgroundColor: borderColor }, opacity_style]}
        />
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  border: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: StyleSheet.hairlineWidth,
  },
});
